import { getConfig } from "../constants/config";
import { getTokenIn } from "../helpers/getTokenIn";
import { getTotalAmountIn } from "../helpers/getTotalAmountIn";
import { parseUnits } from "viem";
import { symphonyAddress } from "../../common/constants/contract";
import { verifyWalletClient } from "../helpers/verifyWalletClient";
import { erc20Abi } from "viem";
import tokenService from "../../common/tokenService";
/**
 * Checks if the Symphony contract has sufficient token allowance using viem.
 * Handles both direct token address checks and route-based checks.
 *
 * @param {Object} params - Check parameters
 * @param {(string|Array)} params.variable - Token address or route array
 *   - If string: Token address to check (e.g., "0x123...")
 *   - If Array: Route array from getRoute
 * @param {import('viem').WalletClient} params.walletClient - Wallet client for checking allowance
 * @param {(string|bigint)} [params.amount] - Amount to check approval for
 *   Required when variable is a token address
 *   - If isRaw=true: Amount in raw units (wei)
 *   - If isRaw=false: Amount in formatted units (e.g., "1.0")
 * @param {boolean} [params.includesNative=false] - Whether the swap includes native token
 * @param {Object} [params.options] - Additional options
 * @param {boolean} [params.options.isRaw=true] - If true, amount is in raw units
 * @param {number} [params.options.chainId=config.chainId] - Chain ID for the check
 *
 * @returns {Promise<boolean>} True if allowance is sufficient
 *
 * @throws {Error} If amount or token address is missing
 * @throws {Error} If token is not listed on Symphony (when using formatted amounts)
 * @throws {Error} If allowance check fails
 *
 * @example
 * // Example 1: Check approval for a route
 * const route = await getRoute({
 *   tokenIn: "0xe30fedd158a2e3b13e9badaeabafc5516e95e8c7", // WSEI
 *   tokenOut: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48", // USDC
 *   amount: "1.0"
 * });
 * const isApproved = await checkApproval({
 *   variable: route,
 *   walletClient: walletClient
 * });
 *
 * @example
 * // Example 2: Check approval for specific token amount
 * const isApproved = await checkApproval({
 *   variable: "0xe30fedd158a2e3b13e9badaeabafc5516e95e8c7", // WSEI
 *   walletClient: walletClient,
 *   amount: 1000000000000000000n, // 1 WSEI in wei
 *   options: { isRaw: true }
 * });
 *
 * @example
 * // Example 3: Check approval with formatted amount
 * const isApproved = await checkApproval({
 *   variable: "0xe30fedd158a2e3b13e9badaeabafc5516e95e8c7", // WSEI
 *   walletClient: walletClient,
 *   amount: "1.0", // 1 WSEI
 *   options: { isRaw: false }
 * });
 *
 * @note Native token handling: If the variable is the native token address, the function returns true immediately.
 */
export const checkApproval = async ({
  variable, //tokenAddress or route !required
  walletClient = undefined, //!required
  amount,
  includesNative = false,
  options = { isRaw: true, chainId: getConfig().chainId },
}) => {
  let amountToCheck;
  let tokenToCheck;
  // Get config for non-token data
  const config = getConfig();
  const {
    publicClient,
    nativeAddress,
    wrappedNativeAddress,
    chainId,
    additionalTokens,
    overrideDefaultTokens,
  } = config;

  // Get updated token list with user's configuration applied
  const tokens = await tokenService.getAsyncTokenList(
    chainId,
    additionalTokens,
    overrideDefaultTokens,
    nativeAddress
  );

  if (variable === nativeAddress) return true;
  // If route given as variable
  if (Array.isArray(variable) && variable.length !== 0) {
    amountToCheck = (await getTotalAmountIn(variable)).amountIn;
    tokenToCheck = getTokenIn(variable);
    if (tokenToCheck === wrappedNativeAddress && includesNative) return true;
  }
  // If tokenAddress is given as a variable
  if (
    !Array.isArray(variable) &&
    typeof variable === "string" &&
    variable.startsWith("0x") &&
    variable.length === 42 &&
    variable !== nativeAddress
  ) {
    tokenToCheck = variable.toLowerCase();

    if (options.isRaw) amountToCheck = amount;
    if (!options.isRaw) {
      if (!tokens[tokenToCheck])
        throw new Error(
          `Token ${variable} data or decimals missing. This might mean token is not listed on Symphony.`
        );
      amountToCheck = parseUnits(amount, tokens[tokenToCheck].attributes.decimals);
    }
  }

  if (!amountToCheck || !tokenToCheck)
    throw new Error(
      `Amount or token address is missing to check approval. Check params passed to checkApproval. amountToCheck: ${amountToCheck},tokenToCheck: ${tokenToCheck}`
    );

  try {
    const verifiedWalletClient = await verifyWalletClient(walletClient);
    const allowance = await publicClient.readContract({
      address: tokenToCheck,
      abi: erc20Abi,
      functionName: "allowance",
      args: [verifiedWalletClient.account.address, symphonyAddress],
    });

    const isTokenApproved = allowance >= amountToCheck;
    return isTokenApproved;
  } catch (error) {
    console.error("Approval check failed:", error.message);
    throw new Error("Approve check failed.");
  }
};
